import React, { useState, useEffect } from 'react';
import { Grid, Button, Select, TextInput } from '../../common';
import GroupLookup from '../../common/form-controls/Lookups/GroupLookup';
import { isRequiredPermissionSet } from '../../../models/entities/AWSAccountGroupTypes';
import { PermissionSetsResponse } from '../../../models/viewModels/AWSAccounts/PermissionSetsResponse';
import { IGroupsService } from '../../../services/IGroupsService';
import Group from '../../../models/Group';
import { Icon } from '../../common/components/Icon';
import { Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import './AccountGroupsEditor.css';

interface RoleGroupPair {
    id: string;
    role: string;
    group: Group | null;
    isLoading?: boolean;
    isRequired?: boolean;
    isOtherRole?: boolean; 
}

interface AccountGroupsEditorProps {
    isManaged: boolean;
    accountGroups: Record<string, string[]>;
    onChange: (accountGroups: Record<string, string[]>) => void;
    disabled?: boolean;
    groupsService: IGroupsService;
    permissionSets: PermissionSetsResponse | null;
    isCreationMode?: boolean;
}

const AccountGroupsEditor: React.FC<AccountGroupsEditorProps> = ({
    isManaged,
    accountGroups,
    onChange,
    disabled = false,
    groupsService,
    permissionSets,
    isCreationMode = false,
}) => {
    const [roleGroupPairs, setRoleGroupPairs] = useState<RoleGroupPair[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [lastAccountGroupsJson, setLastAccountGroupsJson] = useState<string>('');
    const [otherRoleSearches, setOtherRoleSearches] = useState<Record<string, string>>({});

    const getAvailableRoles = (): string[] => {
        if (!permissionSets) return [];
        
        if (isCreationMode) {
            return [];
        } else {
            return [
                ...(permissionSets.Always || []),
                ...(permissionSets.ModificationOnly || []),
                'Other', 
            ];
        }
    };

    const getOtherRoles = (): string[] => {
        return permissionSets?.Other || [];
    };

    const availableRoles = getAvailableRoles();
    const otherRoles = getOtherRoles();

    useEffect(() => {
        setIsInitialized(false);
    }, [isManaged, permissionSets]);

    useEffect(() => {
        const currentJson = JSON.stringify(accountGroups || {});
        if (isInitialized && currentJson !== lastAccountGroupsJson) {
            const currentKeys = Object.keys(accountGroups || {});
            const lastKeys = Object.keys(JSON.parse(lastAccountGroupsJson || '{}'));

            if (
                (lastKeys.length === 0 && currentKeys.length > 0) ||
                currentKeys.length !== lastKeys.length ||
                currentKeys.some((k) => !lastKeys.includes(k))
            ) {
                setIsInitialized(false);
            }
        }
    }, [accountGroups, isInitialized, lastAccountGroupsJson]);

    useEffect(() => {
        const initializePairs = async () => {
            setIsLoading(true);
            const pairs: RoleGroupPair[] = [];
            const otherSearches: Record<string, string> = {};

            setLastAccountGroupsJson(JSON.stringify(accountGroups || {}));

            if (accountGroups && Object.keys(accountGroups).length > 0) {
                for (const [role, groupIds] of Object.entries(accountGroups)) {
                    if (Array.isArray(groupIds)) {
                        for (const groupId of groupIds) {
                            const isAlwaysRole = (permissionSets.Always || []).includes(role);
                            const isModificationOnlyRole = (permissionSets.ModificationOnly || []).includes(role);
                            const isOtherRole = !isAlwaysRole && !isModificationOnlyRole;
                            try {
                                const group = await groupsService.getById(groupId);
                                const pairId = generateUniqueId();
                                
                                pairs.push({
                                    id: pairId,
                                    role: isOtherRole ? 'Other' : role,
                                    group,
                                    isLoading: false,
                                    isRequired: permissionSets ? isRequiredPermissionSet(role, permissionSets) : false,
                                    isOtherRole: isOtherRole,
                                });

                                if (isOtherRole) {
                                    otherSearches[pairId] = role;
                                }
                            } catch (error) {
                                console.error(`Failed to fetch group ${groupId}:`, error);
                                const pairId = generateUniqueId();
                                
                                pairs.push({
                                    id: pairId,
                                    role: isOtherRole ? 'Other' : role,
                                    group: {
                                        id: groupId,
                                        displayName: `Group (${groupId.substring(0, 8)}...)`,
                                    } as Group,
                                    isLoading: false,
                                    isRequired: permissionSets ? isRequiredPermissionSet(role, permissionSets) : false,
                                    isOtherRole: isOtherRole,
                                });

                                if (isOtherRole) {
                                    otherSearches[pairId] = role;
                                }
                            }
                        }
                    }
                }
            }

            setRoleGroupPairs(pairs);
            setOtherRoleSearches(otherSearches);
            setIsInitialized(true);
            setIsLoading(false);
        };

        if (!isInitialized && permissionSets) {
            initializePairs();
        }
    }, [accountGroups, groupsService, isInitialized, permissionSets, otherRoles]);

    const generateUniqueId = (): string => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    const buildAccountGroupsPayload = (pairs: RoleGroupPair[]): Record<string, string[]> => {
        const result: Record<string, string[]> = {};

        for (const pair of pairs) {
            const actualRole = pair.isOtherRole ? otherRoleSearches[pair.id] : pair.role;
            
            if (actualRole && pair.group?.id) {
                if (!result[actualRole]) {
                    result[actualRole] = [];
                }
                if (!result[actualRole].includes(pair.group.id)) {
                    result[actualRole].push(pair.group.id);
                }
            }
        }

        return result;
    };

    const handleAddRole = () => {
        if (availableRoles.length === 0) return;
        
        const defaultRole = availableRoles.find(r => r !== 'Other') || availableRoles[0] || '';
        
        const newPair: RoleGroupPair = {
            id: generateUniqueId(),
            role: defaultRole,
            group: null,
            isRequired: false,
            isOtherRole: false,
        };
        setRoleGroupPairs(prev => [...prev, newPair]);
    };

    const handleRemoveRole = (pairId: string) => {
        const pairToRemove = roleGroupPairs.find(p => p.id === pairId);
        
        if (pairToRemove?.isRequired) {
            const actualRole = pairToRemove.isOtherRole ? otherRoleSearches[pairId] : pairToRemove.role;
            const sameRolePairs = roleGroupPairs.filter(p => {
                const pairActualRole = p.isOtherRole ? otherRoleSearches[p.id] : p.role;
                return pairActualRole === actualRole;
            });
            if (sameRolePairs.length <= 1) {
                return;
            }
        }
        
        const updatedPairs = roleGroupPairs.filter((pair) => pair.id !== pairId);
        const updatedSearches = { ...otherRoleSearches };
        delete updatedSearches[pairId];
        
        setRoleGroupPairs(updatedPairs);
        setOtherRoleSearches(updatedSearches);
        onChange(buildAccountGroupsPayload(updatedPairs));
    };

    const handleRoleChange = (pairId: string, newRole: string) => {
        const isOther = newRole === 'Other';
        const updatedPairs = roleGroupPairs.map((pair) => 
            pair.id === pairId 
                ? { 
                    ...pair, 
                    role: newRole,
                    isRequired: !isOther && permissionSets ? isRequiredPermissionSet(newRole, permissionSets) : false,
                    isOtherRole: isOther,
                } 
                : pair
        );
        
        const updatedSearches = { ...otherRoleSearches };
        if (!isOther) {
            delete updatedSearches[pairId];
        } else if (!updatedSearches[pairId]) {
            updatedSearches[pairId] = '';
        }
        
        setRoleGroupPairs(updatedPairs);
        setOtherRoleSearches(updatedSearches);
        onChange(buildAccountGroupsPayload(updatedPairs));
    };

    const handleOtherRoleSelect = (pairId: string, selectedRole: string) => {
        const updatedSearches = {
            ...otherRoleSearches,
            [pairId]: selectedRole,
        };
        setOtherRoleSearches(updatedSearches);
        
        const payload: Record<string, string[]> = {};
        for (const pair of roleGroupPairs) {
            const actualRole = pair.isOtherRole ? updatedSearches[pair.id] : pair.role;
            if (actualRole && pair.group?.id) {
                if (!payload[actualRole]) {
                    payload[actualRole] = [];
                }
                if (!payload[actualRole].includes(pair.group.id)) {
                    payload[actualRole].push(pair.group.id);
                }
            }
        }
        onChange(payload);
    };

    const handleGroupChange = (pairId: string, newGroup: Group | null) => {
        const updatedPairs = roleGroupPairs.map((pair) => (pair.id === pairId ? { ...pair, group: newGroup } : pair));
        setRoleGroupPairs(updatedPairs);
        onChange(buildAccountGroupsPayload(updatedPairs));
    };

    const roleOptions = availableRoles.map((role) => ({
        value: role,
        text: role,
    }));

    if (!permissionSets) {
        return <p className="no-roles-message">Loading permission sets...</p>;
    }

    if (isCreationMode) {
        return null;
    }

    if (availableRoles.length === 0 && roleGroupPairs.length === 0) {
        return null;
    }

    if (disabled && roleGroupPairs.length === 0 && isInitialized) {
        return null;
    }

    return (
        <div className="account-groups-editor">
            <div className="account-groups-header">
                <h4>Additional Role Groups</h4>
                {!disabled && availableRoles.length > 0 && (
                    <Button 
                        variant="secondary" 
                        size="small" 
                        onClick={handleAddRole} 
                        disabled={disabled}
                    >
                        <Icon icon="plus" size="small" /> Add Permission Set
                    </Button>
                )}
            </div>

            {isLoading && <p className="no-roles-message">Loading permission sets...</p>}

            {!isLoading && roleGroupPairs.length === 0 && !disabled && availableRoles.length > 0 && (
                <p className="no-roles-message">
                    No additional role groups configured. Click "Add Role Group" to add assignments.
                </p>
            )}

            <div className="role-group-pairs">
                {roleGroupPairs.map((pair, index) => (
                    <div key={pair.id} className="role-group-row">
                        {!disabled && pair.role === 'Other' && pair.group && (
                            <p style={{ color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '8px 12px', margin: '0 0 8px 0', fontSize: '0.875rem' }}>
                                ⚠️ <strong>Note:</strong> Permission sets in the <strong>&quot;Other&quot;</strong> category require AWS Platform team approval before they can be granted.
                            </p>
                        )}
                        <Grid variant="halves">
                            <Grid.Item>
                                {disabled ? (
                                    <TextInput
                                        label={index === 0 ? 'Permission Set' : ''}
                                        value={pair.isOtherRole ? otherRoleSearches[pair.id] || '' : pair.role}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                ) : (
                                    <Select
                                        label={index === 0 ? 'Permission Set' : ''}
                                        value={pair.role}
                                        onChange={(e) => handleRoleChange(pair.id, e.target.value)}
                                        options={roleOptions}
                                        disabled={false}
                                        fullWidth
                                    />
                                )}
                                {!disabled && pair.role === 'Other' && (
                                    <div style={{ marginTop: '8px' }}>
                                        <OtherRoleLookup
                                            label="Search Permission Set"
                                            value={otherRoleSearches[pair.id] || ''}
                                            otherRoles={otherRoles}
                                            onSelect={(role) => handleOtherRoleSelect(pair.id, role)}
                                            note="Type to search available permission sets"
                                        />
                                    </div>
                                )}
                            </Grid.Item>
                            <Grid.Item>
                                <div className="group-lookup-with-delete">
                                    <div className="group-lookup-container">
                                        {disabled ? (
                                            <TextInput
                                                label={index === 0 ? 'Group' : ''}
                                                value={pair.group?.displayName || pair.group?.id || ''}
                                                disabled={true}
                                                onChange={() => {}}
                                            />
                                        ) : (
                                            <GroupLookup
                                                label={index === 0 ? 'Group' : ''}
                                                value={pair.group?.displayName || ''}
                                                onChange={(group) => handleGroupChange(pair.id, group)}
                                                placeholder="Search for a group..."
                                                disabled={false}
                                                groupsService={groupsService}
                                            />
                                        )}
                                    </div>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            className="delete-role-btn"
                                            onClick={() => handleRemoveRole(pair.id)}
                                            title="Remove this permission set"
                                        >
                                            <Icon icon="trashcan" size="small" />
                                        </button>
                                    )}
                                </div>
                            </Grid.Item>
                        </Grid>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface OtherRoleLookupProps {
    label: string;
    value: string;
    otherRoles: string[];
    onSelect: (role: string) => void;
    note?: string;
}

const OtherRoleLookup: React.FC<OtherRoleLookupProps> = ({ label, value, otherRoles, onSelect, note }) => {
    const [searchText, setSearchText] = useState<string>(value || '');
    const [filteredResults, setFilteredResults] = useState<string[]>([]);

    useEffect(() => {
        setSearchText(value || '');
    }, [value]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setSearchText(input);

        if (input.trim().length >= 2) {
            const filtered = otherRoles
                .filter(role => role.toLowerCase().includes(input.toLowerCase()))
                .slice(0, 15);
            setFilteredResults(filtered);
        } else {
            setFilteredResults([]);
        }

        // Clear selection if user is typing
        if (input !== value) {
            onSelect('');
        }
    };

    const handleSelect = (selectedValue: string) => {
        const role = filteredResults.find(r => r === selectedValue) || selectedValue;
        setSearchText(role);
        setFilteredResults([]);
        onSelect(role);
    };

    const renderedResults = filteredResults.length > 0 ? (
        filteredResults.map((role) => (
            <TypeaheadItem key={role} value={role}>
                <React.Fragment>
                    <strong>{role}</strong>
                </React.Fragment>
            </TypeaheadItem>
        ))
    ) : searchText.length >= 2 ? (
        <TypeaheadItem value={'__no_results__'}>
            <React.Fragment>
                <i>No permission sets found.</i>
            </React.Fragment>
        </TypeaheadItem>
    ) : null;

    return (
        <Typeahead
            label={label}
            note={note}
            onChange={handleTextChange}
            value={searchText}
            placeholder="Type to search permission sets..."
            clickHandler={(selectedValue) => {
                if (selectedValue !== '__no_results__') {
                    handleSelect(selectedValue);
                }
            }}
        >
            {renderedResults}
        </Typeahead>
    );
};

export default AccountGroupsEditor;
